import { postHelpRequest } from '@/api/capture';
import { SvApiError, serverWords } from '@/api/errors';
import { useT } from '@/i18n/runtime';
import { track } from '@/flow/instrument';
import { useCaptureStore } from '@/store/captureStore';
import { useEffect, useRef, useState } from 'react';
import { Button } from '../components/Button';
import { Icon } from '../components/Icon';
import { DemoArt } from '../demos/DemoArt';
import { openDemo } from '../demos/demoStore';
import { demoCaptionKey, familyDemoFor } from '../demos/demoTypes';
import { useHelp } from './HelpContext';
import { RecoveryCard } from './RecoveryCard';
import { GENERIC_CAPTION_KEY, SKIP_REASONS, type SkipReason, stepTokenFor, whyKeyFor } from './helpCopy';

/**
 * The step help sheet (SPEC_CAPTURE_UX_R2 §7.2 / §7.3), opened by the persistent help button on every screen:
 *   · why this shot — one sentence in the representative's terms (per prompt-key family; the screen's line elsewhere)
 *   · what a good one looks like — the §4 illustration slot with its verb-phrase caption; "Show me" is present but
 *     disabled with the honest note until the §4 assets land (phase 3) — never a fake demonstration
 *   · what to do if you can't — "Skip this" ONLY on a template-optional shot, with the reason picker; and on every
 *     screen "Ask us to call you" → `POST /help-request` → the be's 202 receipt `message` shown VERBATIM (a call,
 *     never a time). Opens no live room; the row lands on the tenant's ops queue.
 *   · when the camera or the location is blocked: the OS-specific recovery card first (§7.4)
 * The sheet is a bottom `<dialog open>` over the screen; the pills beneath are covered, not disabled — closing it
 * returns the representative exactly where they were.
 */
/** `failed.words`: the server's own sentence on a typed refusal (429 rate-limited / 409 closed case), null ⇒ "try again". */
type CallPhase = { kind: 'idle' } | { kind: 'sending' } | { kind: 'sent'; message: string } | { kind: 'failed'; words: string | null };

export function HelpSheet(): JSX.Element | null {
  const { t } = useT();
  const { open, focus, step, hide } = useHelp();
  // FSD-18 D7: a screen that registered no tenant (or none at all) must not leave `{tenantName}` empty — "…कि के
  // रिकॉर्ड…" in Hindi word order. The exchange summary's tenant is the backstop.
  const servedTenant = useCaptureStore((s) => s.summary?.tenantName ?? '');
  const closeRef = useRef<HTMLButtonElement>(null);
  const [reason, setReason] = useState<SkipReason | null>(null);
  const [callReason, setCallReason] = useState('');
  const [call, setCall] = useState<CallPhase>({ kind: 'idle' });

  // A fresh sheet every time it opens; focus lands on the close control (the one that cannot go wrong).
  useEffect(() => {
    if (!open) return;
    setReason(null);
    setCall({ kind: 'idle' });
    closeRef.current?.focus();
  }, [open]);

  if (!open) return null;
  const screen = step?.screen ?? 'other';
  const shot = step?.shot ?? null;
  const onCapture = screen === 'capture' && shot !== null;
  const tenantName = step?.tenantName || servedTenant;
  const blocked = step?.blocked ?? null;
  const why = onCapture ? t(whyKeyFor(shot.promptKey), { tenantName }) : t('sv.help.why.screen', { tenantName });
  const canSkip = onCapture && step?.optional === true && typeof step.onSkip === 'function';
  const demoType = onCapture ? familyDemoFor(shot.promptKey) : null;

  const askForCall = async (): Promise<void> => {
    if (call.kind === 'sending') return;
    setCall({ kind: 'sending' });
    // §10 "Help-request → requests per 1,000 cases, by step": the step token only — never the customer's own words.
    track('help.request', { step: stepTokenFor(screen, shot?.promptKey) });
    try {
      const trimmed = callReason.trim();
      const res = await postHelpRequest({ ...(trimmed ? { reason: trimmed.slice(0, 280) } : {}), step: stepTokenFor(screen, shot?.promptKey) });
      setCall({ kind: 'sent', message: res.message });
    } catch (e) {
      // contract v0.1.13 §11a: on 429 (the per-case bucket) and 409 (a closed case) show the server's words and keep
      // the control; anything else — network, 5xx — is "try again". Never a fabricated receipt.
      const typed = e instanceof SvApiError && (e.status === 429 || e.status === 409) && e.message.trim() !== '';
      setCall({ kind: 'failed', words: typed ? serverWords(e.message) : null });
    }
  };

  const skip = (): void => {
    if (!canSkip || !step?.onSkip) return;
    step.onSkip(reason ?? 'other');
    hide();
  };

  return (
    <>
      <button type="button" className="sv-sheet-scrim" aria-label={t('sv.help.close')} onClick={hide} data-testid="help-scrim" />
      <dialog open className="sv-sheet" aria-labelledby="sv-help-title" data-testid="help-sheet" data-screen={screen}>
        <div className="sv-sheet-head">
          <span className="sv-sheet-title" id="sv-help-title">
            {t('sv.help.title')}
          </span>
          <button ref={closeRef} type="button" className="sv-sheet-close" aria-label={t('sv.help.close')} onClick={hide} data-testid="help-close">
            ×
          </button>
        </div>

        {blocked || focus === 'recovery' ? <RecoveryCard kind={blocked ?? 'camera'} /> : null}

        <section className="sv-sheet-section" data-testid="help-why">
          <div className="pt">
            <Icon name="info" size={14} /> {t('sv.help.why.title')}
          </div>
          <div className="pd">{why}</div>
        </section>

        {onCapture ? (
          <section className="sv-sheet-section" data-testid="help-good">
            <div className="pt">
              <Icon name="check" size={14} /> {t('sv.help.good.title')}
            </div>
            {/* Phase 3: the §4 demonstration is real. The slot shows its first frame as a still preview; "Show me"
                plays it (motion, or the three frames under reduced motion / low data). No demonstration exists for
                a prompt key we do not teach — then the slot keeps the neutral frame and the caption, never a
                fabricated one, and "Show me" is not offered at all (never a dead control). */}
            <div className="sv-ill" data-testid="help-illustration" data-showme={demoType ?? 'none'}>
              {demoType ? <DemoArt type={demoType} frame={0} size={150} /> : <Icon name={shot.kind === 'CLIP' ? 'cam' : 'camera'} size={28} />}
              <span className="sv-ill-cap">{t(demoType ? demoCaptionKey(demoType) : GENERIC_CAPTION_KEY)}</span>
            </div>
            {demoType ? (
              <Button
                variant="soft"
                onClick={() => {
                  track('showme.replay', { type: demoType, where: 'help' });
                  openDemo(demoType, false);
                }}
                data-testid="help-show-me"
              >
                <Icon name="play" size={16} /> {t('sv.help.showMe')}
              </Button>
            ) : null}
          </section>
        ) : null}

        <section className="sv-sheet-section" data-testid="help-cant">
          <div className="pt">
            <Icon name="hand" size={14} /> {t('sv.help.cant.title')}
          </div>
          {canSkip ? (
            <fieldset className="sv-sheet-section" data-testid="help-skip" style={{ border: 0, padding: 0, margin: 0 }}>
              <legend className="pd">{t('sv.help.skip.why')}</legend>
              <div className="sv-reasons">
                {SKIP_REASONS.map((r) => (
                  <label key={r} className="sv-reason" data-on={reason === r ? 'true' : 'false'}>
                    <input type="radio" name="sv-skip-reason" value={r} checked={reason === r} onChange={() => setReason(r)} data-testid={`help-skip-reason-${r}`} />
                    <span>{t(`sv.help.skip.reason.${r}`)}</span>
                  </label>
                ))}
              </div>
              <Button variant="soft" onClick={skip} data-testid="help-skip-confirm">
                <Icon name="skip" size={16} /> {t('sv.help.skip.confirm')}
              </Button>
              <div className="sv-sheet-note">{t('sv.help.skip.note')}</div>
            </fieldset>
          ) : null}

          <div className="pd">{t('sv.help.call.line')}</div>
          {call.kind === 'sent' ? (
            /* <output>: the implicit "status" role — the receipt is announced once, verbatim */
            <output className="sv-receipt" data-testid="help-receipt" aria-live="polite">
              <Icon name="check" size={16} />
              <span>{call.message}</span>
            </output>
          ) : (
            <>
              <textarea
                value={callReason}
                maxLength={280}
                onChange={(e) => setCallReason(e.target.value)}
                aria-label={t('sv.help.call.reason')}
                placeholder={t('sv.help.call.reason')}
                data-testid="help-call-reason"
              />
              <Button onClick={() => void askForCall()} disabled={call.kind === 'sending'} data-testid="help-call">
                {call.kind === 'sending' ? t('sv.help.call.sending') : t('sv.help.call.button')}
              </Button>
              {call.kind === 'failed' ? (
                <div className="consent-note mono" role="alert" data-testid="help-call-failed" data-server-words={call.words ? 'true' : 'false'}>
                  {call.words ?? t('sv.help.call.failed')}
                </div>
              ) : null}
            </>
          )}
        </section>
      </dialog>
    </>
  );
}
